'use client'

import { _log, generateUUID } from '@/lib/utils'
import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { io, Socket } from 'socket.io-client'

type SocketHandler = (message: any) => void

interface SocketContextProps {
  socket: Socket | null
  addHandler: (event: string, handler: SocketHandler) => string
  removeHandler: (id: string) => void
  subscribeToToken: (tokenId: number) => void
  unsubscribeFromToken: (tokenId: number) => void
  subscribeToTokens: (tokenIds: number[]) => void
  unsubscribeFromAllTokens: () => void
  debugRooms: () => void
  socketReady: boolean
}

const SocketContext = createContext<SocketContextProps | undefined>(undefined)

interface SocketProviderProps {
  children: ReactNode
}

interface HandlerEntry {
  id: string
  event: string
  handler: SocketHandler
}

const WebSocketContext = createContext<SocketContextProps | undefined>(undefined)

export const WebSocketProvider = ({ children }: any) => {
  const [socket, setSocket] = useState<any>(null)
  const [handlers, setHandlers] = useState<HandlerEntry[]>([])
  const [subscribedTokens, setSubscribedTokens] = useState<Set<number>>(new Set())

  useEffect(() => {
    console.log('🔵 WebSocketProvider initializing...')

    // Usuń /api z końca NEXT_PUBLIC_API żeby uzyskać base URL
    const apiUrl = process.env.NEXT_PUBLIC_API || ''
    const wsUrl = apiUrl.endsWith('/api') ? apiUrl.slice(0, -4) : apiUrl
    console.log('🔵 NEXT_PUBLIC_API:', process.env.NEXT_PUBLIC_API)
    console.log('🔵 Connecting to WebSocket:', wsUrl)

    const ws = io(wsUrl)
    setSocket(ws)


    ws.on('connect', () => {
      console.log('✅ Socket connection established')
      _log('Socket connection established')
    })

    ws.on('disconnect', () => {
      console.log('❌ Socket connection closed')
      _log('Socket connection closed')
    })

    ws.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error)
    })

    return () => {
      console.log('🔵 Disconnecting WebSocket...')
      ws.disconnect()
    }
  }, [])

  const addHandler = useCallback((event: string, handler: SocketHandler): string => {
    console.log('🔵 Adding handler for event:', event)
    const id = `${event}-${generateUUID()}`
    setHandlers(prevHandlers => {
      socket?.on(event, handler)
      console.log('🔵 Handler added, current handlers:', prevHandlers.length + 1)
      return [...prevHandlers, { id, event, handler }]
    })
    return id
  }, [socket])

  const removeHandler = useCallback((id: string) => {
    setHandlers(prevHandlers => {
      const entry = prevHandlers.find(entry => entry.id === id)
      if (entry) {
        socket?.off(entry.event, entry.handler)
      }
      return prevHandlers.filter(entry => entry.id !== id)
    })
  }, [socket])

  const subscribeToToken = useCallback((tokenId: number) => {
    if (socket) {
      setSubscribedTokens(prev => {
        if (!prev.has(tokenId)) {
          console.log('🔵 Subscribing to token:', tokenId)
          socket.emit('subscribe_to_tokens', { tokenIds: [tokenId] })
          return new Set([...prev, tokenId])
        }
        return prev
      })
    }
  }, [socket])

  const unsubscribeFromToken = useCallback((tokenId: number) => {
    if (socket) {
      setSubscribedTokens(prev => {
        if (prev.has(tokenId)) {
          console.log('🔵 Unsubscribing from token:', tokenId)
          socket.emit('unsubscribe_from_token', { tokenId })
          const newSet = new Set(prev)
          newSet.delete(tokenId)
          return newSet
        }
        return prev
      })
    }
  }, [socket])

  const subscribeToTokens = useCallback((tokenIds: number[]) => {
    if (socket && tokenIds.length > 0) {
      console.log('🔵 Subscribing to tokens:', tokenIds)
      socket.emit('subscribe_to_tokens', { tokenIds })
      setSubscribedTokens(prev => new Set([...prev, ...tokenIds]))
    }
  }, [socket])

  const unsubscribeFromAllTokens = useCallback(() => {
    if (socket) {
      setSubscribedTokens(prev => {
        if (prev.size > 0) {
          console.log('🔵 Unsubscribing from all tokens:', Array.from(prev))
          socket.emit('unsubscribe_from_all_tokens')
          return new Set()
        }
        return prev
      })
    }
  }, [socket])

  const debugRooms = useCallback(() => {
    if (socket) {
      console.log('🔵 Requesting rooms debug info...')
      socket.emit('get_rooms_debug')
      
      // Listen for response once
      const handleRoomsDebug = (data: any) => {
        console.log('🔵 Rooms Debug Info:', data)
        socket.off('rooms_debug', handleRoomsDebug)
      }
      
      socket.on('rooms_debug', handleRoomsDebug)
    }
  }, [socket])

  return (
    <WebSocketContext.Provider 
      value={{ 
        socket, 
        addHandler, 
        removeHandler, 
        subscribeToToken,
        unsubscribeFromToken,
        subscribeToTokens,
        unsubscribeFromAllTokens,
        debugRooms,
        socketReady: socket != null 
      }}
    >
      {children}
    </WebSocketContext.Provider>
  )
}

export const useWebSocket = () => {
  return useContext(WebSocketContext)
}
